import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, MEDIA_TABLE } from '@/lib/supabase';
import type { MediaRecord } from '@/lib/types';
import { formatDateTime } from '@/lib/format';
import { uploadImage } from '@/lib/upload';

interface MediaLibraryTabProps {
  onToast: (msg: string) => void;
}

export default function MediaLibraryTab({ onToast }: MediaLibraryTabProps) {
  const [items, setItems] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from(MEDIA_TABLE)
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setItems((data as MediaRecord[]) || []);
    } catch {
      setError('メディアの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onToast('画像ファイルを選択してください');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      const { error: insertErr } = await supabase
        .from(MEDIA_TABLE)
        .insert({ url, file_name: file.name });
      if (insertErr) throw insertErr;
      onToast('アップロードしました');
      fetchMedia();
    } catch {
      onToast('アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      onToast('URLをコピーしました');
    } catch {
      onToast('コピーに失敗しました');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error: delErr } = await supabase.from(MEDIA_TABLE).delete().eq('id', deleteTarget.id);
      if (delErr) throw delErr;
      onToast('削除しました');
      setDeleteTarget(null);
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
    } catch {
      onToast('削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <p className="text-sm text-[#a0a0b0]">アップロードした画像を管理できます。</p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 disabled:opacity-60 cursor-pointer"
        >
          {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
          アップロード
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {loading ? (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error ? (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-12 text-center">
          <p className="text-[#e8e8f0]">{error}</p>
          <button
            type="button"
            onClick={fetchMedia}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            再読み込み
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-16 text-center">
          <i className="ri-image-2-line text-4xl text-[#7a7a8a]"></i>
          <p className="mt-4 text-[#a0a0b0]">メディアがありません。右上の「アップロード」から追加してください。</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl overflow-hidden group hover:border-[#7c3aed] transition-colors"
            >
              <div className="w-full h-40 bg-[#12121f] flex items-center justify-center overflow-hidden">
                <img
                  src={item.url}
                  alt={item.file_name || 'media'}
                  className="w-full h-40 object-cover object-top"
                />
              </div>
              <div className="p-4">
                <p className="text-sm text-white truncate" title={item.file_name || ''}>
                  {item.file_name || '名称未設定'}
                </p>
                <p className="mt-1 text-xs text-[#a0a0b0]">{formatDateTime(item.created_at) || '-'}</p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopy(item.url)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg h-9 px-3 text-xs font-medium bg-[#12121f] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
                  >
                    <i className="ri-file-copy-line"></i>
                    URLをコピー
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(item)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                    title="削除"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6">
            <h2 className="text-base font-extrabold text-white">削除の確認</h2>
            <p className="mt-3 text-sm text-[#a0a0b0] leading-relaxed">
              「{deleteTarget.file_name || 'この画像'}」をライブラリから削除します。この操作は取り消せません。
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>}
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}